/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURL } from '@/config/serviceAPI';
import { type AxiosInstance } from 'axios';
import { createQuery } from '@/utils/createReactQuery';
import { getAxios } from '@/lib/axios';
import logger from '@/utils/logger';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useAcesDealsVehicleAttributes } from '@/features/deals/hooks/useAcesDealsVehicleAttributes';
import {
  getErrorCode,
  getErrorCodeDescription,
  getErrorTitle,
  isAxiosError,
} from '@/utils/errorsHandling';
import { useQueryResultOnError } from '@/hooks/query/useQueryResultOnError';
import type {
  AcesDealsVehicleAttributes,
  FulfillmentSelectionData,
  FulfillmentSelectionResponse,
} from '../interface';
import { useHeaderData } from '@/features/header/api/getHeader';

type ApiVersion = 'v3' | 'v4';

type OptionsV3 = {
  apiVersion: 'v3';
  storeNumber: string | undefined;
  selectedVehicle: string | undefined;
  skuId: string | undefined;
  dealId: string | undefined;
};

type OptionsV4 = {
  apiVersion: 'v4';
  storeNumber: string | undefined;
  skuId: string | undefined;
  dealId: string | undefined;
  dealsVehicleAttributes: AcesDealsVehicleAttributes | null;
};

type Options = OptionsV3 | OptionsV4;

const urlByVersion: Record<ApiVersion, string> = {
  v3: `${requestBaseURL}/ecomm/b2c/browse/v3/skus`,
  v4: `${requestBaseURL}/ecomm/b2c/browse/v4/skus`,
};

export const getDealDrawerFulfillmentSelection = async (
  options: Options,
  axiosInstance?: AxiosInstance,
) => {
  const { storeNumber, skuId, dealId, apiVersion } = options;

  if (!skuId) {
    throw new Error('skuId is required');
  }

  if (!dealId) {
    throw new Error('dealId is required');
  }
  const storeParam = storeNumber ? `/stores/${storeNumber}` : '';
  const baseUrl = urlByVersion[apiVersion];
  if (apiVersion === 'v3') {
    const { selectedVehicle } = options;
    const vehicleParam = selectedVehicle ? `/vehicles/${selectedVehicle}` : '';

    const response = await getAxios(axiosInstance).get<FulfillmentSelectionResponse>(
      `${baseUrl}/${skuId}/deals/${dealId}/fulfillment-options${vehicleParam}${storeParam}`,
    );
    return selector(response.data);
  } else {
    const { dealsVehicleAttributes } = options;
    const params = dealsVehicleAttributes ? { ...dealsVehicleAttributes } : undefined;
    const response = await getAxios(axiosInstance).get<FulfillmentSelectionResponse>(
      `${baseUrl}/${skuId}/deals/${dealId}/fulfillment-options${storeParam}`,
      {
        params,
      },
    );
    return selector(response.data);
  }
};

const selector = (data: FulfillmentSelectionResponse): FulfillmentSelectionData => data;

const { useData } = createQuery<FulfillmentSelectionData, Options>(
  'dealDrawerFulfillmentSelection',
  getDealDrawerFulfillmentSelection,
);

type PublicOptions = {
  enabled?: boolean;
  skuId: string | undefined;
  dealId: string | undefined;
  selectedVehicle: string | undefined;
};

export const useDealDrawerFulfillmentSelection = ({
  enabled = true,
  skuId,
  dealId,
  selectedVehicle,
}: PublicOptions & { enabled?: boolean }) => {
  const { acesEnabled } = useAcesFlags();
  const dealsVehicleAttributes = useAcesDealsVehicleAttributes();
  const { data: headerData } = useHeaderData();

  const options: Options = acesEnabled
    ? {
        apiVersion: 'v4',
        storeNumber: headerData?.storeNumber,
        skuId,
        dealId,
        dealsVehicleAttributes,
      }
    : {
        apiVersion: 'v3',
        storeNumber: headerData?.storeNumber,
        selectedVehicle,
        skuId,
        dealId,
      };

  const queryResult = useData({
    ...options,
    enabled,
  });

  useQueryResultOnError(queryResult, (err) => {
    if (isAxiosError(err)) {
      const errorCode = getErrorCode(err) ?? err.code;
      const errorCodeDescription = getErrorCodeDescription(err) ?? getErrorTitle(err);

      logger.error({
        message:
          errorCode && errorCodeDescription
            ? `${errorCode}: ${errorCodeDescription}`
            : 'Unknown error',
      });
    }
  });

  return queryResult;
};
